import { type A2uiMessage } from '@a2ui/web_core/v0_9'

import { type CurrencyName } from '@webapp/core/entities/external/Currency/CurrencyName'
import { type ConversationTextVariant } from '../../components/ConversationText'
import {
  type CatalogItemTagIcon,
  type CatalogItemTagVariant,
} from '../CatalogItemTag'
import {
  type CatalogItemVendorBadgeSlotEnum,
  type ConversationCartActionIconEnum,
  type ConversationComponentTypes,
  type ConversationDetailBlockTypes,
  type ConversationDetailIconEnum,
  type ConversationItemKinds,
  type ConversationItemKindEnum,
} from '../enums'

export type ConversationMessageSender = 'user' | 'llm'

export interface ConversationChoiceOption {
  label: string
  value: string
  message: string
}

export interface ConversationMessageComponent {
  type: typeof ConversationComponentTypes.MESSAGE
  sender: ConversationMessageSender
  content: string
  tags?: string[]
}

export interface ConversationConfirmationOptionsComponent {
  type: typeof ConversationComponentTypes.CONFIRMATION_OPTIONS
  options: ConversationChoiceOption[]
}

export interface ConversationPaymentMethodOption {
  id: string
  label: string
  description: Nullable<string>
  badge: Nullable<string>
  isDisabled: boolean
  message: string
}

export interface ConversationPaymentMethodsComponent {
  type: typeof ConversationComponentTypes.PAYMENT_METHODS
  options: ConversationPaymentMethodOption[]
  defaultOptionId: Nullable<string>
  submitText: Nullable<string>
}

export interface ConversationGroupedDropdownOption {
  id: number | string
  name: string
}

export interface ConversationGroupedDropdownGroup {
  id: number | string
  name: string
  options: ConversationGroupedDropdownOption[]
}

export interface ConversationGroupedDropdownComponent {
  type: typeof ConversationComponentTypes.GROUPED_DROPDOWN
  groups: ConversationGroupedDropdownGroup[]
  groupLabel: string
  optionLabel: Nullable<string>
  helperText: Nullable<string>
  defaultGroupId: Nullable<number | string>
  defaultOptionId: Nullable<number | string>
}

export interface ConversationCartItemDelivery {
  label: string
  logoUrl?: string
}

export interface ConversationCartItem {
  id: string
  name: string
  img?: string
  quantity: number
  price: number
  currency?: CurrencyName
  delivery?: ConversationCartItemDelivery
}

export interface ConversationCartAmount {
  price: number
  currency: CurrencyName
}

export interface ConversationCartAddress {
  name: string
  addressLines: string[]
}

export interface ConversationCartMessageAction {
  text: string
  message: string
}

export interface ConversationCartPaymentMethod {
  label: string
  changeAction?: ConversationCartMessageAction
}

export interface ConversationCartDelivery {
  id: string
  estimatedDeliveryDate?: string
  items: ConversationCartItem[]
}

export interface ConversationCartCharge {
  label: string
  amount: ConversationCartAmount
}

export interface ConversationCartVendor {
  vendor?: ConversationVendor
  items: ConversationCartItem[]
  subtotal?: ConversationCartAmount
  deliveries?: ConversationCartDelivery[]
}

export interface ConversationCheckoutVendor extends ConversationCartVendor {
  deliveries: ConversationCartDelivery[]
  shipTo?: ConversationCartAddress
  billTo?: ConversationCartAddress
  paymentMethod?: ConversationCartPaymentMethod
}

export interface ConversationCartAction {
  text: string
  icon?: ConversationCartActionIconEnum
  message?: string
}

export interface ConversationCartComponent {
  type: typeof ConversationComponentTypes.CART
  cartId: string
  version: number
  totalItems: number
  total: Nullable<number>
  currency: CurrencyName
  items: ConversationCartItem[]
  vendors?: ConversationCartVendor[]
  vendor?: ConversationVendor
  totalLabel?: string
  continueAction?: ConversationCartAction
  disclaimer?: string
}

export interface ConversationCheckoutComponent {
  type: typeof ConversationComponentTypes.CHECKOUT
  cartId: string
  version: number
  totalItems: number
  total: Nullable<number>
  currency: CurrencyName
  items: ConversationCartItem[]
  vendors: ConversationCheckoutVendor[]
  charges: ConversationCartCharge[]
  totalLabel?: string
  continueAction?: ConversationCartAction
  disclaimer?: string
}

export type ConversationCartOrCheckoutComponent =
  | ConversationCartComponent
  | ConversationCheckoutComponent

export interface ConversationCatalogItemTag {
  icon?: CatalogItemTagIcon
  text: string
  variant?: CatalogItemTagVariant
}

export interface ConversationVendor {
  id: string
  name: string
  logoUrl?: string
}

export interface ConversationCatalogItemRating {
  value: number
  count: number
  scaleMax: number
}

export interface ConversationCatalogItemVendorBadge {
  slot: CatalogItemVendorBadgeSlotEnum
  text: string
  icon?: CatalogItemTagIcon
  logoUrl?: string
}

export interface ConversationCatalogItemDelivery {
  label: string
}

export interface ConversationVolumePricingSavingsHint {
  savingsPct: number
  label: string
  minQty?: number
}

export interface ConversationVolumePricingTier {
  label: string
  unitPrice: number
  currency: CurrencyName
  savingsPct?: number
  minQty?: number
  maxQty?: number
}

export interface ConversationVolumePricing {
  savingsHint?: ConversationVolumePricingSavingsHint
  tiers?: ConversationVolumePricingTier[]
}

export type ConversationDetailSectionVariant = 'default' | 'callout'

export type ConversationDetailTextVariant = 'body' | 'caption'

export type ConversationDetailTableAlign = 'end'

export interface ConversationDetailTextBlock {
  type: typeof ConversationDetailBlockTypes.TEXT
  content: string
  variant: ConversationDetailTextVariant
}

export interface ConversationDetailKeyValueRow {
  label: string
  value: string
}

export interface ConversationDetailKeyValueBlock {
  type: typeof ConversationDetailBlockTypes.KEY_VALUE
  rows: ConversationDetailKeyValueRow[]
  collapsedAfter?: number
}

export interface ConversationDetailTableColumn {
  label?: string
  align?: ConversationDetailTableAlign
}

export interface ConversationDetailTableBlock {
  type: typeof ConversationDetailBlockTypes.TABLE
  columns: ConversationDetailTableColumn[]
  rows: string[][]
}

export interface ConversationDetailListItem {
  icon?: ConversationDetailIconEnum
  title?: string
  body?: string
}

export interface ConversationDetailListBlock {
  type: typeof ConversationDetailBlockTypes.LIST
  items: ConversationDetailListItem[]
}

export interface ConversationDetailChipOption {
  label: string
  message: string
}

export interface ConversationDetailChipsBlock {
  type: typeof ConversationDetailBlockTypes.CHIPS
  options: ConversationDetailChipOption[]
}

export interface ConversationDetailA2uiBlock {
  type: typeof ConversationDetailBlockTypes.A2UI
  messages: A2uiMessage[]
}

export type ConversationDetailBlock =
  | ConversationDetailTextBlock
  | ConversationDetailKeyValueBlock
  | ConversationDetailTableBlock
  | ConversationDetailListBlock
  | ConversationDetailChipsBlock
  | ConversationDetailA2uiBlock

export interface ConversationDetailSection {
  id: string
  title?: string
  icon?: ConversationDetailIconEnum
  variant: ConversationDetailSectionVariant
  tags?: ConversationCatalogItemTag[]
  blocks: ConversationDetailBlock[]
}

export interface ConversationCatalogItemComponent {
  type: typeof ConversationComponentTypes.CATALOG_ITEM
  itemId: string
  rank?: number
  img?: string
  title: string
  body: string
  sku: string
  price: number
  currency?: CurrencyName
  tags: ConversationCatalogItemTag[]
  detailSections: ConversationDetailSection[]
  images?: string[]
  quantity?: number
  vendor?: ConversationVendor
  rating?: ConversationCatalogItemRating
  vendorBadges?: ConversationCatalogItemVendorBadge[]
  delivery?: ConversationCatalogItemDelivery
  listPrice?: number
  volumePricing?: ConversationVolumePricing
}

export interface ConversationTextComponent {
  type: typeof ConversationComponentTypes.TEXT
  variant: ConversationTextVariant
  content: string
}

export interface ConversationOrderConfirmationComponent {
  type: typeof ConversationComponentTypes.ORDER_CONFIRMATION
  vendorRedirectUrl: Nullable<string>
}

export interface ConversationA2uiComponent {
  type: typeof ConversationComponentTypes.A2UI
  messages: A2uiMessage[]
}

export type ConversationComponent =
  | ConversationMessageComponent
  | ConversationConfirmationOptionsComponent
  | ConversationGroupedDropdownComponent
  | ConversationCartComponent
  | ConversationCheckoutComponent
  | ConversationCatalogItemComponent
  | ConversationTextComponent
  | ConversationOrderConfirmationComponent
  | ConversationPaymentMethodsComponent
  | ConversationA2uiComponent

export interface ConversationMessageItem {
  id: number
  kind: typeof ConversationItemKinds.MESSAGE
  sender: ConversationMessageSender
  content: string
}

export interface ConversationEnvelopeItem {
  id: number
  kind: typeof ConversationItemKinds.ENVELOPE
  envelopeId: string
  components: ConversationComponent[]
  isAnswered: boolean
}

export interface ConversationErrorItem {
  id: number
  kind: typeof ConversationItemKinds.ERROR
  message: string
}

export type ConversationTurnItem =
  | ConversationMessageItem
  | ConversationEnvelopeItem
  | ConversationErrorItem

export type ConversationTurnItemConstructor =
  | Omit<ConversationMessageItem, 'id'>
  | Omit<ConversationEnvelopeItem, 'id'>
  | Omit<ConversationErrorItem, 'id'>

export const createConversationTurnItem = (
  constructor: ConversationTurnItemConstructor,
  id: number
): ConversationTurnItem => ({ ...constructor, id })

export const isConversationItemKind = <K extends ConversationItemKindEnum>(
  item: ConversationTurnItem,
  kind: K
): item is Extract<ConversationTurnItem, { kind: K }> => item.kind === kind
